import { injectable } from 'inversify'

import type {
  AnalyticsProviderInitOptions,
  EcommerceProduct,
  IAnalyticsProvider,
} from '../models'

type YmFn = (counterId: number, method: string, ...args: unknown[]) => void

declare global {
  interface Window {
    ym?: YmFn
    dataLayer?: unknown[]
  }
}

const METRIKA_SCRIPT_SRC = 'https://mc.yandex.ru/metrika/tag.js'

/**
 * Провайдер Яндекс Метрики (ТЗ аналитики §5.3, §5.4).
 * Скрипт счётчика подключается лениво в init() — то есть только после согласия
 * на «аналитика» из CMP. Ecommerce-события пушатся в window.dataLayer в формате
 * GA Enhanced Ecommerce
 */
@injectable()
export class YandexMetrikaProvider implements IAnalyticsProvider {
  private counterId: number | null = null
  private ecommerceEnabled = false

  get initialized() {
    return this.counterId !== null
  }

  init(options: AnalyticsProviderInitOptions) {
    if (this.initialized || typeof window === 'undefined') return

    this.counterId = options.counterId
    this.ecommerceEnabled = options.ecommerceEnabled

    window.dataLayer = window.dataLayer ?? []
    this.injectScript()

    window.ym?.(options.counterId, 'init', {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      ecommerce: options.ecommerceEnabled ? 'dataLayer' : false,
      webvisor: options.webvisorEnabled,
    })
  }

  track(event: string, params?: Record<string, unknown>) {
    if (!this.initialized) return
    window.ym?.(this.counterId!, 'reachGoal', event, params)
  }

  impressions(products: EcommerceProduct[]) {
    this.pushEcommerce('impressions', products)
  }

  detail(product: EcommerceProduct) {
    this.pushEcommerce('detail', { products: [product] })
  }

  add(product: EcommerceProduct) {
    this.pushEcommerce('add', { products: [product] })
  }

  remove(product: EcommerceProduct) {
    this.pushEcommerce('remove', { products: [product] })
  }

  purchase(orderId: string, products: EcommerceProduct[], revenue?: number) {
    this.pushEcommerce('purchase', {
      actionField: { id: orderId, revenue },
      products,
    })
  }

  getClientId(): Promise<string | null> {
    if (!this.initialized || typeof window.ym !== 'function') {
      return Promise.resolve(null)
    }

    return new Promise((resolve) => {
      // Колбэк не вызовется, если счётчик заблокирован — страхуемся таймаутом
      const timeout = setTimeout(() => resolve(null), 3000)
      try {
        window.ym!(this.counterId!, 'getClientID', (clientId: string) => {
          clearTimeout(timeout)
          resolve(clientId ?? null)
        })
      } catch {
        clearTimeout(timeout)
        resolve(null)
      }
    })
  }

  setUserId(userId: string) {
    if (!this.initialized) return
    window.ym?.(this.counterId!, 'setUserID', userId)
  }

  // Лимит контейнера dataLayer на один пуш (ТЗ §5.4)
  private static readonly MAX_CONTAINER_SIZE = 2048

  private pushEcommerce(action: string, payload: unknown) {
    if (!this.initialized || !this.ecommerceEnabled) return

    const container = { currencyCode: 'RUB', [action]: payload }
    if (JSON.stringify(container).length <= YandexMetrikaProvider.MAX_CONTAINER_SIZE) {
      window.dataLayer?.push({ ecommerce: container })
      return
    }

    // Превышение лимита: усекаем необязательные строковые поля товаров,
    // сохраняя ключевые id/price/quantity/position
    const pruned = this.pruneOptionalFields(payload)
    window.dataLayer?.push({
      ecommerce: { currencyCode: 'RUB', [action]: pruned },
    })
  }

  private pruneOptionalFields(payload: unknown): unknown {
    if (typeof payload !== 'object' || payload === null) return payload
    const source = payload as Record<string, unknown>
    if (!Array.isArray(source.products)) return payload

    const optional = ['name', 'brand', 'category', 'variant', 'list']
    const products = (source.products as Record<string, unknown>[]).map((product) => {
      const trimmed = { ...product }
      for (const key of optional) delete trimmed[key]
      return trimmed
    })

    return { ...source, products }
  }

  private injectScript() {
    if (document.querySelector(`script[src="${METRIKA_SCRIPT_SRC}"]`)) return

    // Официальный загрузчик tag.js: очередь вызовов до загрузки скрипта
    const w = window as Window

    const ymStub: any = w.ym ?? function (...args: unknown[]) {
      ;(ymStub.a = ymStub.a || []).push(args)
    }
    ymStub.l = Date.now()
    w.ym = ymStub

    const script = document.createElement('script')
    script.async = true
    script.src = METRIKA_SCRIPT_SRC
    document.head.appendChild(script)
  }
}
